'use client';

import React, { type FC, useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useFormState } from 'react-use-form-state';
import { z } from 'zod';

import Button from 'molecules/Button';
import ContactSidebar from 'molecules/ContactSideBar';
import Textarea from 'molecules/Form/textarea/textarea';
import TextInput from 'molecules/Form/textinput/textinput';

import styles from 'components/Forms/DataPrivacy/dataprivacy.module.scss';

import { formZodSchema } from 'utils/formZodSchema';
import { debounce, isNonEmptyArray } from 'utils/functions';
import { ENV_KEY_MAPS, getEnvValue } from 'utils/getEnvValue';

import type { dataPrivacyFormProps } from 'components/Forms/DataPrivacy/config';
import type { ReCAPTCHAProps } from 'react-google-recaptcha';

const ReCAPTCHAComponent = ReCAPTCHA as unknown as React.ForwardRefExoticComponent<
  React.PropsWithoutRef<ReCAPTCHAProps> & React.RefAttributes<ReCAPTCHA>
>;

// Map form field names (from CMS) to schema keys for first name, last name, and email validation.
// Uses normalized lookup so "First name", "First Name", "first-name", etc. all match.
const SCHEMA_KEYS_BY_NORMALIZED_NAME: Record<string, 'firstName' | 'lastName' | 'email'> = {
  'first name': 'firstName',
  'last name': 'lastName',
  'email address': 'email',
  email: 'email',
};

const getSchemaKey = (fieldName: string): 'firstName' | 'lastName' | 'email' | undefined =>
  SCHEMA_KEYS_BY_NORMALIZED_NAME[fieldName.trim().toLowerCase().replace(/-/g, ' ')];

const PHONE_REGEX = /^(\d{3}-\d{3}-\d{4}|\d{10})$/;

const validateDataPrivacyPhone = (value: string): string => {
  if (!value || value.trim().length === 0) {
    return 'Phone number is required';
  }

  const trimmed = value.trim();
  if (!PHONE_REGEX.test(trimmed)) {
    return 'Phone number must be formatted as xxx-xxx-xxxx or 10 digits.';
  }

  return '';
};

const DataPrivacy: FC<dataPrivacyFormProps> = ({
  title,
  formId = '',
  outline = 'false',
  addPadding,
  formFields,
  redirect_url,
  contactSideBar,
}) => {
  const [formState, { text, email, tel, textarea }] = useFormState();
  const { values } = formState;

  const [formError, setFormError] = useState<string>('');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [inputErrorMessages, setInputErrorMessages] = useState<Record<string, string>>({});

  const [clientId, setClientId] = useState<string>('');
  const [sessionId, setSessionId] = useState<string>('');

  useEffect(() => {
    const fetchCookie = async (cookieName: string, isSessCookie?: boolean) => {
      try {
        const response = await fetch(`/api/cookies/?name=${cookieName}`);
        const data = await response.json();
        if (data.cookie === 'Not found') {
          return;
        }
        if (isSessCookie) {
          setSessionId(data.cookie);
        } else {
          setClientId(data.cookie);
        }
      } catch (error) {
        console.error(`Error fetching cookie "${cookieName}":`, error);
      }
    };

    const isProduction = process.env.NEXT_PUBLIC_DEPLOYMENT_ENV === 'prod';
    const sessID = isProduction ? '_ga_B3QQD8LXK7' : '_ga_1ETK66H7KH';
    fetchCookie('_ga');
    fetchCookie(sessID, true);
  }, []);

  const touched = useRef<Record<string, boolean>>({});
  const changeDebounceMap = useRef<Record<string, ReturnType<typeof debounce>>>({});
  const blurDebounceMap = useRef<Record<string, ReturnType<typeof debounce>>>({});

  const reCaptcha = true;
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '';

  const formUrl = getEnvValue(redirect_url, ENV_KEY_MAPS.urls);

  const requiredFields = isNonEmptyArray(formFields)
    ? formFields
        .filter(field => field.required)
        .map(field => field.fieldName)
        .filter((fieldName): fieldName is string => Boolean(fieldName))
    : [];

  const TEXTAREA_MAX_LENGTH = 1000;

  const validateFieldInput = (
    fieldName: string,
    value: string,
    type: 'text' | 'email' | 'tel' | 'textarea',
    required?: boolean,
  ) => {
    const validationValue = value ?? '';
    const schemaKey = getSchemaKey(fieldName);

    setInputErrorMessages(prev => {
      const next = { ...prev };

      if (type === 'textarea' && validationValue.length > TEXTAREA_MAX_LENGTH) {
        next[fieldName] = 'Field limited to 1000 characters.';
        formState.setFieldError(fieldName, true);
        formState.validity[fieldName] = false;
      } else if (required === true && validationValue.trim().length === 0) {
        next[fieldName] = 'This field is required.';
        formState.setFieldError(fieldName, true);
        formState.validity[fieldName] = false;
      } else if (schemaKey) {
        if (validationValue.trim().length === 0 && required !== true) {
          next[fieldName] = '';
          formState.setFieldError(fieldName, false);
          formState.validity[fieldName] = true;
        } else {
          try {
            const partialSchema = formZodSchema.pick({ [schemaKey]: true } as { [K in typeof schemaKey]: true });
            partialSchema.parse({ [schemaKey]: validationValue });
            next[fieldName] = '';
            formState.setFieldError(fieldName, false);
            formState.validity[fieldName] = true;
          } catch (error) {
            if (error instanceof z.ZodError) {
              const message = error.flatten().fieldErrors[schemaKey]?.[0] ?? 'Invalid input';
              next[fieldName] = message;
              formState.setFieldError(fieldName, true);
              formState.validity[fieldName] = false;
            }
          }
        }
      } else if (type === 'email' && validationValue.trim()) {
        // Fallback for email fields not in nameToSchemaKey
        const validFormat = /^\S+@\S+\.\S+$/.test(validationValue.trim());
        const validDomain = /\.[a-z]{2,}$/i.test(validationValue.trim());
        if (!validFormat) {
          next[fieldName] = 'Invalid email address';
          formState.setFieldError(fieldName, true);
          formState.validity[fieldName] = false;
        } else if (!validDomain) {
          next[fieldName] = 'email must include a valid domain.';
          formState.setFieldError(fieldName, true);
          formState.validity[fieldName] = false;
        } else {
          next[fieldName] = '';
          formState.setFieldError(fieldName, false);
          formState.validity[fieldName] = true;
        }
      } else if (type === 'tel') {
        if (required !== true && validationValue.trim().length === 0) {
          next[fieldName] = '';
          formState.setFieldError(fieldName, false);
          formState.validity[fieldName] = true;
        } else {
          const phoneError = validateDataPrivacyPhone(validationValue);
          if (phoneError) {
            next[fieldName] = phoneError;
            formState.setFieldError(fieldName, true);
            formState.validity[fieldName] = false;
          } else {
            next[fieldName] = '';
            formState.setFieldError(fieldName, false);
            formState.validity[fieldName] = true;
          }
        }
      } else {
        next[fieldName] = '';
        formState.setFieldError(fieldName, false);
        formState.validity[fieldName] = true;
      }

      const hasError = Object.values(next).some(msg => msg.length > 0);
      // Use the value being validated for this field so we don't rely on stale form state
      const currentValues = { ...values, [fieldName]: validationValue };
      const requiredFieldsEntered = requiredFields.every(field => {
        const val = currentValues[field];

        return typeof val === 'string' && val.trim().length > 0;
      });

      if (hasError) {
        setFormError('Please correct the highlighted fields.');
        setFormStatus('error');
      } else if (!requiredFieldsEntered && next[fieldName]) {
        // Only show "complete all required" when this field has an error (e.g. required and empty)
        // so we don't show it when the user is just typing in one field and others are still empty
        setFormError('Please complete all required fields.');
        setFormStatus('error');
      } else {
        setFormError('');
        setFormStatus('success');
      }

      return next;
    });
  };

  const validateOnChange = (
    fieldName: string,
    value: string,
    type: 'text' | 'email' | 'tel' | 'textarea',
    required?: boolean,
  ) => {
    touched.current[fieldName] = true;
    if (!changeDebounceMap.current[fieldName]) {
      changeDebounceMap.current[fieldName] = debounce((latestValue: string) => {
        if (touched.current[fieldName]) {
          validateFieldInput(fieldName, latestValue, type, required);
        }
      }, 200);
    }
    changeDebounceMap.current[fieldName](value);
  };

  const validateOnBlur = (
    fieldName: string,
    value: string,
    type: 'text' | 'email' | 'tel' | 'textarea',
    required?: boolean,
  ) => {
    if (!blurDebounceMap.current[fieldName]) {
      touched.current[fieldName] = true;
      blurDebounceMap.current[fieldName] = debounce((latestValue: string) => {
        validateFieldInput(fieldName, latestValue, type, required);
      }, 200);
    }
    blurDebounceMap.current[fieldName](value);
  };

  const handleCaptchaChange = async (token: string | null) => {
    try {
      if (!token) {
        setFormError('Please complete the reCAPTCHA.');

        return;
      }

      const res = await fetch('/api/recaptcha', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setCaptchaToken(token);
      } else {
        setFormError('reCAPTCHA verification failed.');
      }
    } catch {
      setFormError('Error verifying reCAPTCHA. Please try again.');
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const honeypotValue = (e.target as HTMLFormElement).username?.value;
    if (honeypotValue && honeypotValue.trim() !== '') {
      e.preventDefault();
      setFormStatus('error');

      return;
    }

    const hasFieldErrors = Object.values(inputErrorMessages).some(msg => msg.length > 0);
    let hasRequiredErrors = false;
    requiredFields.forEach(field => {
      const value = values[field];
      if (!value || (typeof value === 'string' && value.trim().length === 0)) {
        hasRequiredErrors = true;
      }
    });

    if (hasFieldErrors || hasRequiredErrors || formStatus === 'error') {
      e.preventDefault();
      setFormError(hasFieldErrors ? 'Please correct the highlighted fields.' : 'Please fill out all required fields.');
      setFormStatus('error');

      return;
    }

    if (reCaptcha && !captchaToken) {
      e.preventDefault();
      setFormError('Please complete the reCAPTCHA.');
      setFormStatus('error');

      return;
    }

    setFormStatus('success');
    setFormError('');
  };

  const getFormStateProps = (type: 'text' | 'email' | 'tel', fieldName: string) => {
    const handlers: Record<'text' | 'email' | 'tel', typeof text> = { text, email, tel };

    return {
      ...handlers[type]({
        name: fieldName,
        validateOnBlur: true,
      }),
    };
  };

  return (
    <div className={`${styles.containerWrapper} ${addPadding ? styles.addPadding : styles.removePadding}`}>
      <div className={styles.containerText}>
        <div>
          <form
            id={formId}
            className={styles.form}
            name={formId}
            onSubmit={handleSubmit}
            action={formUrl}
            method="POST"
            noValidate
          >
            <input type="hidden" name="form-name" value={title} />
            <input type="hidden" name="clientId" value={clientId} />
            <input type="hidden" name="sessionId" value={sessionId} />
            <input
              type="text"
              name="username"
              id="username"
              className={styles.username}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />
            <fieldset className={outline === true ? styles.border : styles.borderNone}>
              {title && <legend className={styles.legend}>{title}</legend>}
              {formError && <p className={styles.errorText}>{formError}</p>}

              {isNonEmptyArray(formFields) &&
                formFields
                  .filter(
                    (field): field is typeof field & { type: 'textarea' } =>
                      Boolean(field.fieldName) && field.type === 'textarea',
                  )
                  .map(({ fieldName = '', label = '', required }) => (
                    <div key={fieldName} className={styles.textareaSection}>
                      <Textarea
                        id={fieldName}
                        label={label}
                        required={required}
                        noAutoSize
                        className={styles.textareaField}
                        message={inputErrorMessages[fieldName]}
                        formStateProps={{
                          ...textarea({
                            name: fieldName,
                            validateOnBlur: true,
                          }),
                        }}
                        validateOnBlur={(name: string, value: string, isRequired: boolean) =>
                          validateOnBlur(name, value, 'textarea', isRequired)
                        }
                        validateOnChange={(name: string, value: string, isRequired: boolean) =>
                          validateOnChange(name, value, 'textarea', isRequired)
                        }
                        valid={!inputErrorMessages[fieldName]}
                      />
                    </div>
                  ))}

              <div
                className={
                  isNonEmptyArray(formFields) && formFields.some(f => f.type === 'textarea')
                    ? `${styles.grid} ${styles.gridAfterTextarea}`
                    : styles.grid
                }
              >
                {isNonEmptyArray(formFields) &&
                  formFields
                    .filter(field => Boolean(field.fieldName) && field.type !== 'textarea')
                    .map(({ type = 'text', fieldName = '', label = '', required }) => (
                      <div key={fieldName}>
                        {(type === 'text' || type === 'tel' || type === 'email') && fieldName && (
                          <TextInput
                            id={fieldName}
                            label={label}
                            maxLength={120}
                            required={required}
                            message={inputErrorMessages[fieldName]}
                            validateOnBlur={(name: string, value: string) =>
                              validateOnBlur(name, value, type, required)
                            }
                            validateOnChange={(name: string, value: string) =>
                              validateOnChange(name, value, type, required)
                            }
                            formStateProps={getFormStateProps(type, fieldName)}
                            valid={!inputErrorMessages[fieldName]}
                          />
                        )}
                      </div>
                    ))}
              </div>
              {reCaptcha && (
                <ReCAPTCHAComponent ref={recaptchaRef} sitekey={siteKey as string} onChange={handleCaptchaChange} />
              )}

              <Button id="data-privacy-form-submit-button" color="teal" type="submit" name="submit">
                Submit
              </Button>
            </fieldset>
          </form>
        </div>
      </div>
      <div className={styles.sideNav}>{contactSideBar && <ContactSidebar {...contactSideBar} />}</div>
    </div>
  );
};

export default DataPrivacy;
